import { forwardRef, useEffect, useRef, useState } from 'react'
import { Application } from 'pixi.js'
import '@esotericsoftware/spine-pixi-v8'
import { BetsCycleLoader } from '@/components/ui/betscycle-loader'
import { ChickenBetPanel } from '@/components/original/games/ui/ChickenBetPanel'
import { ResultModal } from '@/components/original/games/chicken/components/ResultModal'
import { AssetManager } from '@/components/original/games/chicken/core/AssetManager'
import { ChickenScene } from '@/components/original/games/chicken/core/ChickenScene'
import { GameController } from '@/components/original/games/chicken/core/GameController'
import { SoundManager } from '@/components/original/games/chicken/core/SoundManager'
import { cn } from '@/lib/utils'
import { useAppDispatch, useAppSelector } from '@/store/hooks'
import {
	dismissChickenResult,
	resetChickenGame,
	selectChickenGame,
	setChickenCurrencyCode,
} from '@/store/slices/chickenSlice'

const DISMISS_RESULT_DELAY_MS = 3000

interface ChickenGameProps {
	currencyCode: string
	className?: string
}

export const ChickenGame = forwardRef<HTMLDivElement, ChickenGameProps>(function ChickenGame({ currencyCode, className }, ref) {
	const dispatch = useAppDispatch()
	const chicken = useAppSelector(selectChickenGame)
	const hostRef = useRef<HTMLDivElement>(null)
	const appRef = useRef<Application | null>(null)
	const sceneRef = useRef<ChickenScene | null>(null)
	const controllerRef = useRef<GameController | null>(null)
	const [controller, setController] = useState<GameController | null>(null)
	const [isLoading, setIsLoading] = useState(true)
	const [loadError, setLoadError] = useState(false)
	const [loadProgress, setLoadProgress] = useState(0)

	const setRootRef = (node: HTMLDivElement | null) => {
		if (typeof ref === 'function') {
			ref(node)
		} else if (ref) {
			ref.current = node
		}
	}

	useEffect(() => {
		dispatch(setChickenCurrencyCode(currencyCode))
	}, [currencyCode, dispatch])

	useEffect(() => {
		sceneRef.current?.setMultiplierLabel(chicken.currentMultiplier)
	}, [chicken.currentMultiplier, chicken.step])

	useEffect(() => {
		if (!controller) {
			return
		}

		void controller.loadMultipliers(chicken.difficulty)
	}, [chicken.difficulty, controller])

	useEffect(() => {
		if (chicken.phase === 'live' || chicken.isAnimating || chicken.autoBetRunning) {
			return
		}

		sceneRef.current?.rebuildLevel(chicken.difficulty, chicken.multiplierSteps)
	}, [chicken.difficulty])

	useEffect(() => {
		sceneRef.current?.setMultiplierTable(chicken.multiplierSteps)
	}, [chicken.multiplierSteps])

	useEffect(() => {
		if (chicken.phase !== 'ready' || chicken.isAnimating || chicken.isResultVisible || chicken.autoBetRunning) {
			return
		}

		sceneRef.current?.resetRound()
	}, [chicken.phase, chicken.isAnimating, chicken.isResultVisible, chicken.autoBetRunning])

	useEffect(() => {
		const syncRendererToHost = () => {
			const app = appRef.current
			const host = hostRef.current
			if (!app || !host) {
				return
			}

			const { clientWidth: width, clientHeight: height } = host
			if (width <= 0 || height <= 0) {
				return
			}

			app.renderer.resize(width, height)
		}

		document.addEventListener('fullscreenchange', syncRendererToHost)
		return () => document.removeEventListener('fullscreenchange', syncRendererToHost)
	}, [])

	useEffect(() => {
		let cancelled = false
		const host = hostRef.current

		if (!host) {
			return
		}

		const boot = async () => {
			setIsLoading(true)
			setLoadError(false)

			try {
				await AssetManager.loadAll((progress) => {
					if (!cancelled) {
						setLoadProgress(Math.round(progress * 100))
					}
				})
				await SoundManager.instance.load()

				if (cancelled || !hostRef.current) {
					return
				}

				const app = new Application()
				await app.init({
					background: '#1a2332',
					resizeTo: hostRef.current,
					antialias: true,
					autoDensity: true,
					resolution: Math.min(window.devicePixelRatio || 1, 2),
				})

				if (cancelled) {
					app.destroy(true)
					return
				}

				const canvas = app.canvas
				canvas.style.display = 'block'
				canvas.style.width = '100%'
				canvas.style.height = '100%'
				hostRef.current.appendChild(canvas)
				appRef.current = app

				const scene = new ChickenScene(app)
				sceneRef.current = scene

				const gameController = new GameController(scene)
				controllerRef.current = gameController
				setController(gameController)
				setIsLoading(false)
			} catch {
				if (!cancelled) {
					setLoadError(true)
					setIsLoading(false)
				}
			}
		}

		void boot()

		return () => {
			cancelled = true
			controllerRef.current?.dispose()
			controllerRef.current = null
			setController(null)
			sceneRef.current?.dispose()
			sceneRef.current = null
			SoundManager.instance.stopAll()

			if (appRef.current) {
				appRef.current.destroy(true)
				appRef.current = null
			}

			if (host) {
				host.replaceChildren()
			}

			dispatch(resetChickenGame())
		}
	}, [dispatch])

	useEffect(() => {
		if (!chicken.isResultVisible) {
			return
		}

		const timer = window.setTimeout(() => {
			dispatch(dismissChickenResult())
			sceneRef.current?.resetRound()
		}, DISMISS_RESULT_DELAY_MS)

		return () => window.clearTimeout(timer)
	}, [dispatch, chicken.isResultVisible])

	return (
		<div
			ref={setRootRef}
			className={cn(
				'flex w-full min-w-0 flex-col gap-4 xl:flex-row',
				'[&:fullscreen]:fixed [&:fullscreen]:inset-0 [&:fullscreen]:z-[9999] [&:fullscreen]:flex [&:fullscreen]:h-screen [&:fullscreen]:w-screen [&:fullscreen]:flex-col [&:fullscreen]:gap-4 [&:fullscreen]:bg-background [&:fullscreen]:p-4 xl:[&:fullscreen]:flex-row',
				className,
			)}
		>
			<div className='order-2 h-[420px] w-full shrink-0 overflow-hidden sm:h-[520px] xl:order-1 xl:h-[520px] xl:w-[340px] 2xl:w-[360px] [:fullscreen_&]:order-2 [:fullscreen_&]:h-auto [:fullscreen_&]:max-h-[42%] [:fullscreen_&]:w-full [:fullscreen_&]:shrink-0 xl:[:fullscreen_&]:order-1 xl:[:fullscreen_&]:h-full xl:[:fullscreen_&]:max-h-none xl:[:fullscreen_&]:w-[340px]'>
				<ChickenBetPanel controller={controller} className='h-full min-h-0 [:fullscreen_&]:h-full' />
			</div>

			<div
				className={cn(
					'relative order-1 w-full min-w-0 overflow-hidden rounded-2xl border border-border bg-card',
					'aspect-[6/5]',
					'xl:order-2 xl:aspect-auto xl:min-h-[520px] xl:flex-1',
					'[:fullscreen_&]:order-1 [:fullscreen_&]:aspect-auto [:fullscreen_&]:h-auto [:fullscreen_&]:min-h-0 [:fullscreen_&]:flex-1 xl:[:fullscreen_&]:order-2 xl:[:fullscreen_&]:h-full',
				)}
			>
				<div ref={hostRef} className='absolute inset-0 bg-[#1a2332] [&_canvas]:block [&_canvas]:h-full [&_canvas]:w-full' />

				{isLoading ? (
					<div className='absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 bg-card/90'>
						<BetsCycleLoader size={32} />
						<p className='text-sm font-medium text-muted-foreground'>Loading chicken assets… {loadProgress}%</p>
					</div>
				) : null}

				{loadError ? (
					<div className='absolute inset-0 z-10 flex items-center justify-center bg-card p-6'>
						<p className='text-sm text-muted-foreground'>Unable to load the chicken game right now.</p>
					</div>
				) : null}

				<ResultModal visible={chicken.isResultVisible} result={chicken.currentResult} currencyCode={chicken.currencyCode} />
			</div>
		</div>
	)
})
